/* ==========================================================================
   Cart drawer behaviour.

   Adding to cart opens the drawer instead of navigating away — most orders
   here are one unit and the next action is checkout.

   Everything degrades: with JS off, the cart pill is an ordinary link to
   /cart, quantity steppers are absent, and Shopify's own cart page handles it.
   ========================================================================== */

(() => {
  const drawer = document.getElementById('gp-cart-drawer');
  if (!drawer) return;

  const body = drawer.querySelector('[data-gp-cart-body]');
  const openers = () => document.querySelectorAll('[data-gp-cart-open]');

  // The cart pill is a real link to /cart. Only hijack it once we know the
  // drawer exists, so the no-JS path stays intact.
  openers().forEach((el) => {
    el.setAttribute('data-gp-drawer-open', '');
    el.setAttribute('aria-controls', 'gp-cart-drawer');
    el.setAttribute('aria-expanded', 'false');
  });

  /* ---- Re-render from the server ---------------------------------------
     Section Rendering API: ask Shopify for the drawer's HTML rather than
     rebuilding the DOM here, so money formatting, discounts and translations
     stay server-side and correct. */
  async function refresh() {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=gp-cart-drawer`);
    if (!res.ok) return;

    const html = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = html.getElementById('gp-cart-drawer');
    if (!fresh) return;

    // Swap only the inner regions — replacing the whole node would drop the
    // open state and the focus trap's element references.
    const panel = drawer.querySelector('.gp-cart-drawer__panel');
    const freshPanel = fresh.querySelector('.gp-cart-drawer__panel');
    if (panel && freshPanel) panel.innerHTML = freshPanel.innerHTML;

    const count = fresh.querySelector('[data-gp-cart-count]')?.textContent?.trim();
    if (count != null) {
      document.querySelectorAll('[data-gp-cart-count]').forEach((n) => (n.textContent = count));
    }
  }

  async function setQuantity(key, quantity) {
    drawer.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error(`cart/change ${res.status}`);
      await refresh();
    } catch (err) {
      // A failed quantity change must not leave a stale number on screen —
      // reload so what the buyer sees matches what Shopify holds.
      console.error('[gp-cart]', err);
      window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    } finally {
      drawer.removeAttribute('aria-busy');
    }
  }

  drawer.addEventListener('click', (e) => {
    const step = e.target.closest('[data-gp-qty]');
    if (!step) return;
    e.preventDefault();
    setQuantity(step.dataset.gpKey, Number(step.dataset.gpQty));
  });

  /* ---- Open on add-to-cart ---------------------------------------------
     Intercepts any product form posting to /cart/add. Sections built in later
     phases get this for free by using a normal product form. */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action.includes('/cart/add')) return;

    e.preventDefault();
    const button = form.querySelector('[type="submit"]');
    button?.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      if (!res.ok) {
        // Sold out, or a variant that no longer exists. Let the native form
        // post so Shopify renders its own error rather than us inventing one.
        form.submit();
        return;
      }

      await refresh();
      window.gpDrawer?.open(drawer, form.querySelector('[data-gp-cart-open]') || undefined);
    } catch (err) {
      console.error('[gp-cart]', err);
      form.submit();
    } finally {
      button?.removeAttribute('aria-busy');
    }
  });
})();
